import { ValidationError } from '../errors/HikvisionError';

/**
 * Configuration validator interface
 */
export interface ConfigValidator {
  // eslint-disable-next-line no-unused-vars
  validate<T>(config: unknown, schema: ConfigSchema<T>): T;
}

/**
 * Configuration schema definition
 */
export interface ConfigSchema<T> {
  required: (keyof T)[];
  optional?: (keyof T)[];
  // eslint-disable-next-line no-unused-vars
  validators?: Partial<Record<keyof T, (value: any) => boolean>>;
}

/**
 * Default configuration validator implementation
 */
export class DefaultConfigValidator implements ConfigValidator {
  validate<T>(config: unknown, schema: ConfigSchema<T>): T {
    if (!config || typeof config !== 'object') {
      throw new ValidationError('Configuration must be an object');
    }

    const configObj = config as Record<string, any>;
    const result = {} as T;

    // Check required fields
    for (const field of schema.required) {
      const key = field as string;
      if (!(key in configObj) || configObj[key] === undefined || configObj[key] === null) {
        throw new ValidationError(`Required field '${key}' is missing`);
      }
      (result as any)[key] = configObj[key];
    }

    // Check optional fields
    if (schema.optional) {
      for (const field of schema.optional) {
        const key = field as string;
        if (key in configObj && configObj[key] !== undefined) {
          (result as any)[key] = configObj[key];
        }
      }
    }

    // Run custom validators
    if (schema.validators) {
      for (const [field, validator] of Object.entries(schema.validators)) {
        const key = field as keyof T;
        const value = (result as any)[key];
        if (value !== undefined && validator && typeof validator === 'function' && !validator(value)) {
          throw new ValidationError(`Validation failed for field '${String(key)}'`);
        }
      }
    }

    return result;
  }
}